import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, ButtonIcon, Dropdown } from '@quintoandar-tokko/jopijs';
import styles from '../filterBar.module.scss';
import Trans from '@/components/shared/Trans';
import translations from '../FilterBar.trans.json';
import {
  normalizeText,
  sortByKeyEs,
  formatSelectedText,
  getIsAllSelected,
  toBranchScopeItem,
} from '../utils';
import { setSelectedIds } from '@/store/slices/components/filterBar/FilterBarSlice';
import FilterSelectControl, {
  useFilterSelection,
} from '../FilterSelectControl';
import { BRANCH, BRANCH_LABEL_KEY } from '../constants/filtersConstants';

export const MIN_BRANCHES_TO_SHOW_DROPDOWN = 2;

function BranchesDropdown() {
  const dispatch = useDispatch();
  const { selectedIds, selectBranches } = useSelector(
    (state) => state.FilterBarSlice.filters
  );
  const [selectedLocalIds, setSelectedLocalIds] = useState([]);
  const [searchText, setSearchText] = useState('');

  const branchTitle = Trans(translations, 'Branches');
  const normalizedSearch = normalizeText(searchText);
  const filteredBranches = sortByKeyEs(
    selectBranches.filter((branch) =>
      normalizeText(branch[BRANCH_LABEL_KEY]).includes(normalizedSearch)
    ),
    BRANCH_LABEL_KEY
  );

  const toggleSelection = (branch) => {
    setSelectedLocalIds((prev) => {
      const isSelected = prev.find((x) => x.id === branch.id);
      if (isSelected) {
        return prev.filter((x) => x.id !== branch.id);
      }
      return [
        ...prev,
        { id: branch.id, text: branch.name, filter_type: BRANCH },
      ];
    });
  };

  const valueTextButton = formatSelectedText(selectedIds, branchTitle, BRANCH);

  const handleBranchAdd = () => {
    dispatch(setSelectedIds({ idList: selectedLocalIds, type: BRANCH }));
  };

  useEffect(() => {
    const branchesSelec = selectedIds.filter((id) => id.filter_type === BRANCH);
    return setSelectedLocalIds(branchesSelec);
  }, [selectedIds]);

  const allBranchItems = selectBranches.map(toBranchScopeItem);

  const isAllSelected = getIsAllSelected(selectedIds, selectBranches, BRANCH);
  const selection = useFilterSelection(BRANCH, {
    allLabel: Trans(translations, 'AllBranches'),
    allSelected: isAllSelected,
  });
  const isAllLocalSelected = getIsAllSelected(
    selectedLocalIds,
    selectBranches,
    BRANCH
  );

  const selectAll = () => {
    if (!selectBranches.length) return;

    if (isAllLocalSelected) {
      setSelectedLocalIds([]);
      return;
    }

    setSelectedLocalIds(allBranchItems);
  };

  if (selectBranches.length < MIN_BRANCHES_TO_SHOW_DROPDOWN) {
    return null;
  }

  return (
    <Dropdown height="auto" className={`dropdown ${styles.dropdownList}`}>
      <FilterSelectControl
        filled={selection.filled}
        pending={selectedLocalIds.length > 0}
        tooltip={selection.tooltip}
        onClear={selection.onClear}
        clearLabel={selection.clearLabel}
      >
        <Dropdown.Button
          variant="dropdownDefault"
          className={styles.dropdownButton}
          text={
            isAllSelected ? Trans(translations, 'AllBranches') : valueTextButton
          }
        />
      </FilterSelectControl>
      <Dropdown.Items clickClose height={336} className={styles.dropdownItems}>
        <Dropdown.Search
          placeholder={Trans(translations, 'SearchBranch')}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          suffix={
            searchText !== '' && (
              <ButtonIcon
                variant={['transparentIcon', 'iconExtraSmall22px']}
                icon="icon-cerrar"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchText('');
                }}
              />
            )
          }
        />
        {!searchText && (
          <Dropdown.Multiselect
            key={`all-branches-${isAllLocalSelected}`}
            onClick={(e) => {
              e.stopPropagation();
              selectAll();
            }}
            active={isAllLocalSelected}
            className={styles.dropdownMultiselect}
          >
            {Trans(translations, 'AllBranches')}
          </Dropdown.Multiselect>
        )}
        {filteredBranches.map((branch) => {
          const isActive = selectedLocalIds.find((x) => x.id === branch.id);

          return (
            <Dropdown.Multiselect
              key={`${branch.id}-${isActive ? '1' : '0'}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleSelection(branch);
              }}
              active={isActive}
              className={styles.dropdownMultiselect}
            >
              {branch.name}
            </Dropdown.Multiselect>
          );
        })}
        {filteredBranches.length === 0 && (
          <Dropdown.Text>{Trans(translations, 'NoResultsFound')}</Dropdown.Text>
        )}
        <Box className={styles.applyButtonContainer}>
          <Button
            className={styles.applyButton}
            variant={['primary', 'small']}
            onClick={handleBranchAdd}
          >
            {Trans(translations, 'Apply')}
          </Button>
        </Box>
      </Dropdown.Items>
    </Dropdown>
  );
}

export default BranchesDropdown;
